import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  AlertTriangle, 
  RefreshCw, 
  WifiOff, 
  Server, 
  Shield,
  Bug,
  Home
} from 'lucide-react';
import { ErrorType, type AppError } from '@/lib/errors';
import { cn } from '@/lib/utils';

interface ErrorDisplayProps {
  error: AppError | Error | string;
  onRetry?: () => void;
  onDismiss?: () => void;
  className?: string;
  variant?: 'inline' | 'card' | 'page';
  showDetails?: boolean;
}

export function ErrorDisplay({ 
  error, 
  onRetry, 
  onDismiss, 
  className,
  variant = 'inline',
  showDetails = false 
}: ErrorDisplayProps) {
  const getErrorInfo = (error: AppError | Error | string) => {
    if (typeof error === 'string') {
      return {
        title: 'Error',
        message: error,
        icon: AlertTriangle,
        type: ErrorType.UNKNOWN_ERROR,
      };
    }

    if (error instanceof Error && 'type' in error) {
      const appError = error as AppError;
      switch (appError.type) {
        case ErrorType.NETWORK_ERROR:
          return {
            title: 'Network Error',
            message: appError.message,
            icon: WifiOff,
            type: appError.type,
          };
        case ErrorType.AUTHENTICATION_ERROR:
          return {
            title: 'Authentication Error',
            message: appError.message,
            icon: Shield,
            type: appError.type,
          };
        case ErrorType.SESSION_NOT_FOUND:
          return {
            title: 'Session Not Found',
            message: appError.message,
            icon: AlertTriangle,
            type: appError.type,
          };
        case ErrorType.AGENT_ERROR:
          return {
            title: 'Agent Error',
            message: appError.message,
            icon: Server,
            type: appError.type,
          };
        case ErrorType.VALIDATION_ERROR:
          return {
            title: 'Validation Error',
            message: appError.message,
            icon: AlertTriangle,
            type: appError.type,
          };
        default:
          return {
            title: 'Unknown Error',
            message: appError.message,
            icon: Bug,
            type: appError.type,
          };
      }
    }

    return {
      title: 'Error',
      message: error.message || 'An unexpected error occurred',
      icon: AlertTriangle,
      type: ErrorType.UNKNOWN_ERROR,
    };
  };

  const errorInfo = getErrorInfo(error);
  const Icon = errorInfo.icon;

  const getVariantStyles = () => {
    switch (errorInfo.type) {
      case ErrorType.NETWORK_ERROR:
        return 'border-orange-200 bg-orange-50 text-orange-900 dark:border-orange-800 dark:bg-orange-900 dark:text-orange-100';
      case ErrorType.AUTHENTICATION_ERROR:
        return 'border-red-200 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-900 dark:text-red-100';
      case ErrorType.AGENT_ERROR:
        return 'border-purple-200 bg-purple-50 text-purple-900 dark:border-purple-800 dark:bg-purple-900 dark:text-purple-100';
      default:
        return 'border-destructive bg-destructive/10 text-destructive';
    }
  };

  if (variant === 'page') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="max-w-lg w-full">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
              <Icon className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle className="text-xl">{errorInfo.title}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className={getVariantStyles()}>
              <Icon className="h-4 w-4" />
              <AlertDescription>{errorInfo.message}</AlertDescription>
            </Alert>

            <div className="flex gap-2 justify-center">
              {onRetry && (
                <Button onClick={onRetry} variant="outline">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Try Again
                </Button>
              )}
              <Button onClick={() => window.location.href = '/'}>
                <Home className="h-4 w-4 mr-2" />
                Go Home
              </Button>
            </div>

            {showDetails && error instanceof Error && (
              <details className="mt-4">
                <summary className="cursor-pointer text-sm font-medium text-muted-foreground">
                  Error Details
                </summary>
                <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-auto">
                  {error.stack}
                </pre>
              </details>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (variant === 'card') {
    return (
      <Card className={cn('border-destructive', className)}>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-destructive">
            <Icon className="h-5 w-5" />
            {errorInfo.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">{errorInfo.message}</p>
          
          {(onRetry || onDismiss) && (
            <div className="flex gap-2">
              {onRetry && (
                <Button onClick={onRetry} size="sm" variant="outline">
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Retry
                </Button>
              )}
              {onDismiss && (
                <Button onClick={onDismiss} size="sm" variant="ghost">
                  Dismiss
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Inline variant (default)
  return (
    <Alert className={cn(getVariantStyles(), className)}>
      <Icon className="h-4 w-4" />
      <AlertTitle>{errorInfo.title}</AlertTitle>
      <AlertDescription className="mt-2">
        {errorInfo.message}
        {(onRetry || onDismiss) && (
          <div className="flex gap-2 mt-3">
            {onRetry && (
              <Button onClick={onRetry} size="sm" variant="outline">
                <RefreshCw className="h-3 w-3 mr-1" />
                Retry
              </Button>
            )}
            {onDismiss && (
              <Button onClick={onDismiss} size="sm" variant="ghost">
                Dismiss
              </Button>
            )}
          </div>
        )}
      </AlertDescription>
    </Alert>
  );
}

// Specialized error components
export function NetworkErrorDisplay({ onRetry }: { onRetry?: () => void }) {
  return (
    <ErrorDisplay
      error={{
        name: 'NetworkError',
        message: 'Unable to connect to the server. Please check your internet connection.',
        type: ErrorType.NETWORK_ERROR,
      } as AppError}
      onRetry={onRetry}
      variant="card"
    />
  );
}

export function MaintenanceDisplay() {
  return (
    <ErrorDisplay
      error="The system is currently under maintenance. Please try again later."
      variant="page"
    />
  );
}

export function NotFoundDisplay() {
  return (
    <ErrorDisplay
      error="The page you're looking for doesn't exist."
      variant="page"
    />
  );
}